import React from "react";
import { fethContractStats } from "../../../services/partner.service.ts";
import { ContractStats } from "../../../models/ContractStats.ts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

interface ContractsSummaryProps {
  compact?: boolean;
  height?: number;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div style={styles.customTooltip}>
        <p style={styles.tooltipLabel}>{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} style={{ color: entry.color, margin: "4px 0" }}>
            {`${entry.name}: ${entry.value}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const ContractsSummary: React.FC<ContractsSummaryProps> = ({
  compact,
  height,
}) => {
  const [loading, setLoading] = React.useState(true);
  const [stats, setStats] = React.useState<ContractStats | null>(null);

  const fetchData = async () => {
    try {
      const response = await fethContractStats();
      setStats(response);
    } catch (error) {
      console.error("Error fetching contract stats:", error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchData();
  }, []);

  if (loading || !stats) {
    return (
      <div style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <div style={styles.chartHeaderContent}>
            <h3 style={styles.chartTitle}>
              <i
                className="fa-solid fa-file-signature"
                style={styles.chartTitleIcon}
              ></i>
              Contratos firmados
            </h3>
          </div>
        </div>
        <div
          style={{
            ...styles.chartBody,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "32px",
          }}
        >
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "24px", color: "#6b7280" }}
          ></i>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.chartCard}>
      <div style={styles.chartHeader}>
        <div style={styles.chartHeaderContent}>
          <h3 style={styles.chartTitle}>
            <i
              className="fa-solid fa-file-signature"
              style={styles.chartTitleIcon}
            ></i>
            Contratos firmados
          </h3>
          <div style={styles.headerStats}>
            <div style={styles.headerPill}>
              <span style={styles.headerPillLabel}>Total</span>
              <span style={styles.headerPillValue}>{stats.totalPartners}</span>
            </div>
            <div
              style={{
                ...styles.headerPill,
                background: "rgba(34,197,94,0.06)",
                borderColor: "rgba(34,197,94,0.35)",
              }}
            >
              <span
                style={{ ...styles.headerPillDot, background: "#22c55e" }}
              />
              <span style={styles.headerPillLabel}>Firmados</span>
              <span style={styles.headerPillValue}>
                {stats.partnersWithContract}
              </span>
            </div>
            <div
              style={{
                ...styles.headerPill,
                background: "rgba(234,179,8,0.06)",
                borderColor: "rgba(234,179,8,0.4)",
              }}
            >
              <span
                style={{ ...styles.headerPillDot, background: "#eab308" }}
              />
              <span style={styles.headerPillLabel}>Sin firma</span>
              <span style={styles.headerPillValue}>
                {stats.partnersWithoutContract}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div style={styles.chartBody}>
        <div
          style={{
            width: "100%",
            height: height ?? (compact ? 220 : 280),
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stats.stats.map((item) => ({
                name: item.name,
                "Con Contrato": item.withContract,
                "Sin Contrato": item.withoutContract,
              }))}
              margin={{ top: 10, right: 20, left: 0, bottom: 70 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis
                dataKey="name"
                angle={-45}
                textAnchor="end"
                height={80}
                tick={{ fontSize: 11, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                tick={{ fontSize: 12, fill: "#6b7280" }}
                tickLine={false}
                axisLine={false}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend />
              <Bar
                dataKey="Con Contrato"
                stackId="a"
                fill="#22c55e"
                radius={[0, 0, 0, 0]}
              />
              <Bar
                dataKey="Sin Contrato"
                stackId="a"
                fill="#eab308"
                radius={[8, 8, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  chartCard: {
    background: "#fff",
    borderRadius: "16px",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.08)",
    overflow: "hidden",
    width: "100%",
  },
  chartHeader: {
    background: "linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)",
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  chartHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
  },
  chartTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 600,
    color: "#1f2937",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  chartTitleIcon: {
    fontSize: "18px",
    color: "#2563eb",
  },
  chartBody: {
    padding: "16px 20px 20px 20px",
  },
  headerStats: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  headerPill: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 8px",
    borderRadius: "999px",
    border: "1px solid #e5e7eb",
    background: "#ffffff",
  },
  headerPillDot: {
    width: "8px",
    height: "8px",
    borderRadius: "999px",
  },
  headerPillLabel: {
    fontSize: "11px",
    color: "#6b7280",
    fontWeight: 500,
  },
  headerPillValue: {
    fontSize: "12px",
    color: "#111827",
    fontWeight: 600,
  },
  customTooltip: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
  tooltipLabel: {
    fontWeight: 600,
    marginBottom: "8px",
    color: "#2d3748",
  },
};

export default ContractsSummary;
